import { useEffect, useRef } from "react";
import type { RefTarget } from "../snak/SnakContext";
import { REFERENCES } from "../data/references";
import { Sheet } from "./Sheet";
import { Placeholder } from "./Placeholder";
import { MINT_LIMITATIONS } from "../data/limitations";
export function ReferencesSheet({
  open,
  target,
  onClose
}: {
  open: boolean;
  target: RefTarget;
  onClose: () => void;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open || target == null) return;
    const id = typeof target === "number" ? `ref-${target}` : `sec-${target}`;
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        block: "start",
        behavior: "smooth"
      });
    }, 120);
    return () => clearTimeout(t);
  }, [open, target]);
  return <Sheet open={open} onClose={onClose} kicker="Sources & information" title="References, limitations and prescribing information" data-id="element-183" data-mp="17q63vm5u66f9">
      <div ref={bodyRef} className="space-y-8" data-id="element-184" data-mp="322mawp6ghol6">
        <section data-id="element-185" data-mp="3209skawsdc7l">
          <h3 className="text-[12px] font-semibold text-ink" data-id="element-186" data-mp="2m6l8p3ojxcij">References</h3>
          <ol className="mt-3 space-y-4" data-id="element-187" data-mp="2m4gvtw8od364">
            {REFERENCES.map(r => {
            const active = target === r.n;
            return <li key={r.n} id={`ref-${r.n}`} className={`scroll-mt-4 border-l-2 pl-3 transition-colors duration-300 ${active ? "border-teal" : "border-rule"}`} data-id="element-188" data-mp="2uz529ppzkqwi">
                  <p className="text-[13px] leading-relaxed text-charcoal" data-id="element-189" data-mp="237z8rs4rtrg7">
                    <span className="tabular mr-1.5 font-semibold text-ink" data-id="element-190" data-mp="1y9nvt8j6j0tr">{r.n}.</span>
                    {r.citation}
                  </p>
                  {r.note && <p className="mt-1 text-[12px] text-muted" data-id="element-191" data-mp="279aqlbhel8du">{r.note}</p>}
                  {r.placeholder && <p className="mt-1.5" data-id="element-192" data-mp="3nnapqk8fcu01">
                      <Placeholder data-id="element-193" data-mp="3nlzkefzbze6t">{r.placeholder}</Placeholder>
                    </p>}
                </li>;
          })}
          </ol>
        </section>

        <section id="sec-limitations" className="scroll-mt-4" data-id="element-194" data-mp="2ga9vlbdm4l8z">
          <h3 className="text-[12px] font-semibold text-ink" data-id="element-195" data-mp="2l9warqd04bkf">MINT study limitations</h3>
          <ul className="mt-3 space-y-2 text-[13px]" data-id="element-196" data-mp="2pbq5li0imlco">
            {MINT_LIMITATIONS.map(l => <li key={l} className="flex gap-2" data-id="element-197" data-mp="2uaud847skb0w">
                <span className="mt-[9px] h-px w-3 shrink-0 bg-charcoal" data-id="element-198" data-mp="2u9j9u5i9d5ab" />
                <span data-id="element-199" data-mp="1xld2hatybysc">{l}</span>
              </li>)}
          </ul>
        </section>

        <section id="sec-pi" className="scroll-mt-4" data-id="element-200" data-mp="21ldrtqil9ue6">
          <h3 className="text-[12px] font-semibold text-ink" data-id="element-201" data-mp="21k2kjgpxq4ib">Prescribing and safety information</h3>
          <p className="mt-3 text-[13px]" data-id="element-202" data-mp="26j5laoyvtokh">
            Before prescribing, consult the current locally applicable Summary of Product Characteristics / Fachinformation for
            POTELIGEO® (mogamulizumab), including posology, contraindications, special warnings, precautions and adverse
            reactions.<sup className="ml-0.5 text-teal" data-id="element-203" data-mp="1itb5pzn6hlwj">4</sup>
          </p>
          <dl className="mt-4 divide-y divide-rule border-y border-rule text-[13px]" data-id="element-204" data-mp="1bbmr9x4wf85v">
            {[["Germany", "Pflichttext / Fachinformation"], ["Austria", "Fachinformation / Pflichtangaben"], ["Switzerland", "Swissmedic-approved Fachinformation"]].map(([c, what]) => <div key={c} className="flex flex-col gap-1 py-2.5" data-id="element-205" data-mp="xeq1lz0inwqk">
                <dt className="w-24 shrink-0 font-medium text-ink" data-id="element-206" data-mp="sevco10axh85">{c}</dt>
                <dd data-id="element-207" data-mp="nmanxdw95ubh">
                  <Placeholder data-id="element-208" data-mp="nkzht7fc4ah7">Insert {what}, including mandatory safety information</Placeholder>
                </dd>
              </div>)}
          </dl>
          <p className="mt-3 text-[12px] text-muted" data-id="element-209" data-mp="dowevzcns7t7">
            Adverse events should be reported in accordance with national requirements.{" "}
            <Placeholder data-id="element-210" data-mp="9lzqfs0fthhc">Insert national reporting contacts (BfArM / BASG / Swissmedic) and company contact</Placeholder>
          </p>
        </section>

        <section id="sec-compliance" className="scroll-mt-4 rounded-lg bg-amber-soft p-4" data-id="element-211" data-mp="24pfs01my0j6z">
          <h3 className="text-[12px] font-semibold text-amber" data-id="element-212" data-mp="28s2ls836ce6t">Concept prototype</h3>
          <p className="mt-2 text-[13px] text-charcoal" data-id="element-213" data-mp="1v05ziko6syic">
            This experience is a design concept. It is not approved HCP promotional material and has not undergone medical,
            legal or regulatory review. MINT figures are provisional and must be verified against the full final study
            materials. MINT is an observational, non-randomised study: no superiority over other treatments and no causal
            relationship is implied.
          </p>
          <p className="mt-2 text-[12px] text-charcoal" data-id="element-214" data-mp="1c3d3whrjrlku">
            Intended audience: healthcare professionals in Germany, Austria and Switzerland.{" "}
            <Placeholder data-id="element-215" data-mp="36f1i2hpyfk46">Insert job code, date of preparation and local HCP-only declaration</Placeholder>
          </p>
        </section>
      </div>
    </Sheet>;
}